import React, { useContext, useState } from 'react';
import { 
    Box, Typography, Button, Paper, Grid, Divider, List, 
    ListItem, ListItemIcon, ListItemText 
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Hook y Componentes QUD
import { useFacebookSDK } from '../../../hooks/useFacebookSDK'; // Ajusta la ruta según tu estructura
import QUDProgress from '../../../components/progress'; // Ajusta la ruta según tu estructura
import { sendMetaCodeToBackend } from '../../../controllers/company/ai/AiController';
import UserContext from '../../../navigation/context/userContext';

// Importa aquí tu controlador cuando lo crees
// import { sendMetaCodeToBackend } from '../../../controllers/company/ai/AiController'; 

const MetaOnboarding = ({ planName, metaAppId, metaConfigId, onConnectionSuccess }) => {
    
    const { userData } = useContext(UserContext);
    // Inicializa el SDK dinámicamente
    const isSdkLoaded = useFacebookSDK(metaAppId);
    
    // Estado para el loader mientras el backend procesa el código
    const [isProcessing, setIsProcessing] = useState(false);

    const handleConnectWhatsApp = () => {
        if (!isSdkLoaded) {
            return;
        }

        window.FB.login((response) => {
            if (response.authResponse && response.authResponse.code) {
                const code = response.authResponse.code;
                processMetaCode(code);
            } else {
                console.log("El usuario canceló el flujo.");
            }
        }, {
            config_id: metaConfigId, // Usamos el Config ID que viene de la BD
            response_type: 'code', 
            override_default_response_type: true
        });
    };


    const processMetaCode = async (code) => {
        setIsProcessing(true);
        try {
            
            const response = await sendMetaCodeToBackend(userData.entity.id,{ code: code });
            
            if (response.success) {
                if (onConnectionSuccess) onConnectionSuccess();
            } else {
                alert(response.message || "Error al registrar la cuenta.");
            }

        } catch (error) {
            console.error("Error validando el código con el servidor", error);
            alert("Ocurrió un error de conexión al vincular la cuenta. Inténtalo de nuevo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Muestra tu loader personalizado mientras procesa
    if (isProcessing) return <QUDProgress open={isProcessing} />;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
            <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
                
                <Box textAlign="center" mb={4}>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        ¡Tu plan <Box component="span" color="primary.main">{planName}</Box> está activo!
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        El último paso es vincular tu número de teléfono con la API oficial de WhatsApp.
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    
                    {/* COLUMNA IZQUIERDA: Advertencias e Instrucciones */}
                    <Grid item xs={12} md={7}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Antes de comenzar, ten en cuenta:
                        </Typography>
                        
                        <List>
                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                    <CheckCircleOutlineIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Usa un número dedicado" 
                                    secondary="Recomendamos usar un número de teléfono que no esté siendo utilizado actualmente en la aplicación personal de WhatsApp ni en WhatsApp Business tradicional."
                                    primaryTypographyProps={{ fontWeight: 'bold' }}
                                />
                            </ListItem>
                            
                            <Divider component="li" sx={{ my: 1 }} />
                            
                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                    <WarningAmberIcon color="warning" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Si usas un número existente..." 
                                    secondary="Deberás eliminar la cuenta en la app de tu teléfono ANTES de continuar. Una vez vinculado, los chats se gestionarán 100% a través de la plataforma y los Agentes."
                                    primaryTypographyProps={{ fontWeight: 'bold' }}
                                />
                            </ListItem>

                            <Divider component="li" sx={{ my: 1 }} />

                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                    <InfoIcon color="info" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Ten tu teléfono a la mano" 
                                    secondary="Meta te enviará un código SMS o una llamada al número que vas a registrar para verificar que eres el propietario."
                                    primaryTypographyProps={{ fontWeight: 'bold' }}
                                />
                            </ListItem>
                        </List>
                    </Grid>

                    {/* COLUMNA DERECHA: Call to Action (Botón de Meta) */}
                    <Grid item xs={12} md={5}>
                        <Box 
                            sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                bgcolor: '#f8f9fa',
                                p: 4,
                                borderRadius: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="h1" fontSize={60} mb={2}>🚀</Typography>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                ¿Todo listo?
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                Haz clic en el botón para abrir la configuración segura de Meta.
                            </Typography>

                            <Button 
                                variant="contained" 
                                size="large" 
                                onClick={handleConnectWhatsApp}
                                disabled={!isSdkLoaded} 
                                fullWidth
                                sx={{ 
                                    bgcolor: '#25D366', 
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    '&:hover': { bgcolor: '#128C7E' } 
                                }}
                            >
                                {isSdkLoaded ? 'Conectar con Facebook' : 'Cargando módulo...'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

            </Paper>
        </Box>
    );
};

export default MetaOnboarding;